import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { attendanceService, batchService, userService, auth } from '../services';
import { SECONDARY_TRADES } from '../services/studentService';
import {
  resolveInstructorStudents,
  getAssignedTrades,
  getStudentTrade,
  normalizeTradeCode,
} from '../utils/instructorScope';
import {
  getTradeFromRecord,
  recordMatchesScope,
  studentsToRoll,
  loadBatchStudentsByTrade,
  filterRunningSecondaryBatches,
  ATTENDANCE_LEVEL_OPTIONS,
  studentMatchesLevel,
  recordMatchesLevel,
} from '../utils/secondaryAttendance';
import { generateAttendanceReportPdf, buildAttendanceExcelRows } from '../utils/attendanceReportPdf';

const getGeneratedByFooter = () => {
  const user = auth.getUser();
  const name = user?.fullName || user?.username || 'Instructor';
  const date = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `Generated by ${name} on ${date}`;
};

const isStudentAttendanceLocked = (studentId, existingRecords, instructorScope) => {
  if (!instructorScope) return false;
  const id = String(studentId);
  return (Array.isArray(existingRecords) ? existingRecords : []).some(
    (record) => String(record.student?._id || record.student) === id
  );
};

const AttendanceManagement = ({ filterByInstructorId = false, useInstructorTradeScope = false }) => {
  const isSecondaryRoll = useInstructorTradeScope || !filterByInstructorId;
  const [viewMode, setViewMode] = useState('roll');
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedTrade, setSelectedTrade] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [attendanceByStudent, setAttendanceByStudent] = useState({});
  const [existingRecords, setExistingRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRoll, setLoadingRoll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [attendanceByDate, setAttendanceByDate] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const [expandedDate, setExpandedDate] = useState(null);
  const [reviewJumpDate, setReviewJumpDate] = useState('');
  const [instructorContext, setInstructorContext] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (viewMode === 'review') loadReviewByDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filterByInstructorId, selectedBatchId, selectedTrade, selectedLevel, instructorContext?.batch?._id]);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (selectedDate && selectedDate > todayStr) setSelectedDate(todayStr);
  }, [selectedDate]);

  const instructorAssignedTrades = getAssignedTrades(instructorContext);
  const activeBatchId = filterByInstructorId && useInstructorTradeScope
    ? (instructorContext?.batch?._id || instructorContext?.batch || '')
    : selectedBatchId;
  const activeBatch = filterByInstructorId && useInstructorTradeScope
    ? instructorContext?.batch
    : batches.find((b) => b._id === selectedBatchId);
  const tradeOptions = filterByInstructorId && useInstructorTradeScope
    ? instructorAssignedTrades
    : SECONDARY_TRADES;
  const rollReady = isSecondaryRoll
    ? !!activeBatchId
    : false;

  useEffect(() => {
    if (viewMode !== 'roll' || !selectedDate) {
      setEnrolledStudents([]);
      setAttendanceByStudent({});
      setExistingRecords([]);
      return;
    }
    if (isSecondaryRoll) {
      if (rollReady) loadRollCall();
      return;
    }
    setEnrolledStudents([]);
    setAttendanceByStudent({});
    setExistingRecords([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedBatchId,
    selectedTrade,
    selectedLevel,
    selectedDate,
    viewMode,
    filterByInstructorId,
    useInstructorTradeScope,
    rollReady,
    instructorContext?.batch?._id,
  ]);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 4000); return () => clearTimeout(t); }
  }, [successMessage]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  const loadReviewByDate = async () => {
    setReviewLoading(true); setError('');
    try {
      const all = filterByInstructorId && useInstructorTradeScope
        ? await attendanceService.getInstructorAttendance(auth.getUserId())
        : await attendanceService.getAllAttendance();
      let list = Array.isArray(all) ? all : [];

      if (isSecondaryRoll) {
        if (activeBatchId) {
          list = list.filter((r) => recordMatchesScope(r, { batchId: activeBatchId }));
        }
        if (selectedTrade) {
          list = list.filter((r) => recordMatchesScope(r, { trade: selectedTrade }));
        } else if (filterByInstructorId && instructorAssignedTrades.length > 0) {
          const assignedCodes = instructorAssignedTrades.map(normalizeTradeCode);
          list = list.filter((r) => {
            const code = normalizeTradeCode(getTradeFromRecord(r));
            return !code || assignedCodes.includes(code);
          });
        }
        if (selectedLevel) {
          list = list.filter((r) => recordMatchesLevel(r, selectedLevel));
        }
      }

      const byDate = {};
      list.forEach(r => {
        const dateStr = r.date ? new Date(r.date).toISOString().split('T')[0] : 'Unknown';
        if (!byDate[dateStr]) byDate[dateStr] = [];
        byDate[dateStr].push(r);
      });
      const sortedDates = Object.keys(byDate).sort((a, b) => (a > b ? -1 : 1));
      const sorted = {};
      sortedDates.forEach(d => { sorted[d] = byDate[d]; });
      setAttendanceByDate(sorted);
      if (sortedDates.length > 0) setExpandedDate(sortedDates[0]);
    } catch (err) { setError(err.error || err.message || 'Failed to load attendance'); }
    finally { setReviewLoading(false); }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true); setError('');
      if (filterByInstructorId && useInstructorTradeScope) {
        const userId = auth.getUserId();
        const ctx = await userService.getInstructorContext(userId);
        const loginTrades = auth.getUser()?.assignedTrades;
        const assignedTrades = getAssignedTrades(ctx).length
          ? getAssignedTrades(ctx)
          : Array.isArray(loginTrades)
            ? loginTrades
            : [];
        setInstructorContext({ ...ctx, assignedTrades });
        if (assignedTrades.length === 1) setSelectedTrade(assignedTrades[0]);
        setBatches([]);
        setSelectedBatchId('');
        setLoading(false);
        return;
      }

      let list = [];
      try {
        const running = await batchService.getRunningBatches();
        list = filterRunningSecondaryBatches(running);
      } catch {
        /* fall through */
      }
      if (list.length === 0) {
        const all = await batchService.getAllBatches({ track: 'secondary' });
        list = filterRunningSecondaryBatches(all);
      }
      setBatches(list);
      if (list.length > 0 && !selectedBatchId) setSelectedBatchId(list[0]._id);
    } catch (err) { setError(err.error || err.message || 'Failed to load batches'); }
    finally { setLoading(false); }
  };

  const loadRollCall = async () => {
    if (!isSecondaryRoll || !activeBatchId) return;

    setLoadingRoll(true); setError(''); setSuccessMessage('');
    try {
      let scopedStudents = [];

      if (filterByInstructorId && useInstructorTradeScope) {
        const userId = auth.getUserId();
        let ctx = instructorContext || (await userService.getInstructorContext(userId));
        const loginTrades = auth.getUser()?.assignedTrades;
        if (!getAssignedTrades(ctx).length && Array.isArray(loginTrades) && loginTrades.length) {
          ctx = { ...ctx, assignedTrades: loginTrades };
        }
        if (!instructorContext) setInstructorContext(ctx);
        scopedStudents = await resolveInstructorStudents(ctx);
        if (selectedTrade) {
          scopedStudents = scopedStudents.filter((s) =>
            normalizeTradeCode(getStudentTrade(s)) === normalizeTradeCode(selectedTrade)
          );
        }
        if (selectedLevel) {
          scopedStudents = scopedStudents.filter((s) => studentMatchesLevel(s, selectedLevel));
        }
      } else {
        scopedStudents = await loadBatchStudentsByTrade(activeBatchId, selectedTrade, selectedLevel);
      }

      const students = studentsToRoll(scopedStudents);
      setEnrolledStudents(students);

      const allAttendance = filterByInstructorId && useInstructorTradeScope
        ? await attendanceService.getInstructorAttendance(auth.getUserId())
        : await attendanceService.getAllAttendance();

      const dayRecords = (Array.isArray(allAttendance) ? allAttendance : []).filter((r) =>
        recordMatchesScope(r, {
          batchId: activeBatchId,
          trade: selectedTrade || undefined,
          date: selectedDate,
        })
      );
      setExistingRecords(dayRecords);

      const byStudent = {};
      students.forEach(({ studentId }) => {
        const existing = dayRecords.find((r) => (r.student?._id || r.student) === studentId);
        byStudent[studentId] = existing ? existing.status : 'present';
      });
      setAttendanceByStudent(byStudent);
    } catch (err) {
      setError(err.error || err.message || 'Failed to load roll call');
    } finally {
      setLoadingRoll(false);
    }
  };

  const setStatus = (studentId, status) => {
    if (isStudentAttendanceLocked(studentId, existingRecords, filterByInstructorId && useInstructorTradeScope)) return;
    setAttendanceByStudent((prev) => ({ ...prev, [studentId]: status }));
  };
  const markAll = (status) => {
    const instructorScope = filterByInstructorId && useInstructorTradeScope;
    const next = { ...attendanceByStudent };
    enrolledStudents.forEach(({ studentId }) => {
      if (isStudentAttendanceLocked(studentId, existingRecords, instructorScope)) return;
      next[studentId] = status;
    });
    setAttendanceByStudent(next);
  };
  const getTodayDateString = () => new Date().toISOString().split('T')[0];

  const saveAttendance = async () => {
    if (!selectedDate || !activeBatchId) return;
    if (selectedDate > getTodayDateString()) { setError('Attendance cannot be marked for future dates.'); return; }
    setSaving(true); setError(''); setSuccessMessage('');
    try {
      const instructorScope = filterByInstructorId && useInstructorTradeScope;
      const studentsToSave = instructorScope
        ? enrolledStudents.filter(
            ({ studentId }) => !isStudentAttendanceLocked(studentId, existingRecords, true)
          )
        : enrolledStudents;

      if (instructorScope && studentsToSave.length === 0) {
        setError('Attendance is already saved for all students on this date.');
        return;
      }

      const records = studentsToSave.map(({ studentId, trade }) => ({
        student: studentId,
        status: attendanceByStudent[studentId] || 'present',
        trade: selectedTrade || (trade && trade !== '—' ? trade : undefined),
      }));

      if (filterByInstructorId && useInstructorTradeScope) {
        const result = await attendanceService.markInstructorAttendance({
          instructorId: auth.getUserId(),
          date: selectedDate,
          records,
        });
        if (Array.isArray(result?.errors) && result.errors.length > 0) {
          const alreadySaved = result.errors.filter((e) =>
            String(e.error || '').toLowerCase().includes('already recorded')
          ).length;
          if (alreadySaved > 0 && (result.saved?.length || 0) === 0) {
            setError('Attendance is already saved for the selected students on this date.');
          } else {
            setError(`Saved ${result.saved?.length || 0} student(s); ${result.errors.length} could not be saved.`);
          }
        } else {
          setSuccessMessage(`Attendance saved for ${result?.saved?.length || records.length} student(s).`);
        }
        loadRollCall();
        return;
      }

      try {
        const result = await attendanceService.markBatchAttendance({
          batchId: activeBatchId,
          trade: selectedTrade || undefined,
          date: selectedDate,
          records,
        });
        if (Array.isArray(result?.errors) && result.errors.length > 0) {
          setError(`Saved with ${result.errors.length} issue(s). Check student trade assignments.`);
        } else {
          setSuccessMessage('Attendance saved successfully.');
        }
      } catch {
        const recordByStudent = new Map(existingRecords.map(r => [(r.student?._id || r.student), r]));
        for (const { studentId, trade } of enrolledStudents) {
          const status = attendanceByStudent[studentId] || 'present';
          const tradeCode = selectedTrade || (trade && trade !== '—' ? trade : undefined);
          const payload = {
            student: studentId,
            batch: activeBatchId,
            trade: tradeCode,
            date: selectedDate,
            status,
          };
          const existing = recordByStudent.get(studentId);
          if (existing) { await attendanceService.updateAttendance(existing._id, payload); }
          else { await attendanceService.createAttendance(payload); }
        }
        setSuccessMessage('Attendance saved successfully.');
      }
      loadRollCall();
    } catch (err) { setError(err.error || err.message || 'Failed to save attendance'); }
    finally { setSaving(false); }
  };

  const rollScopeLabel = [
    selectedTrade || null,
    selectedLevel || null,
    activeBatch?.name || null,
  ].filter(Boolean).join(' — ') || (activeBatch?.name || 'Cohort');
  const tradeLabel = instructorAssignedTrades.length > 0
    ? (selectedTrade || instructorAssignedTrades.join(', '))
    : (selectedTrade || '');

  const getFieldLabel = () => {
    const field = selectedTrade
      || (instructorAssignedTrades.length > 0 ? instructorAssignedTrades.join(', ') : 'All fields');
    return selectedLevel ? `${field} · ${selectedLevel}` : field;
  };

  const exportAttendancePDF = async () => {
    if (!activeBatch || enrolledStudents.length === 0) return;
    const titleExtra = [selectedTrade, selectedLevel].filter(Boolean).map((v) => ` — ${v}`).join('');
    const present = enrolledStudents.filter(({ studentId }) => (attendanceByStudent[studentId] || 'present') === 'present').length;
    const absent = enrolledStudents.length - present;
    const pct = enrolledStudents.length > 0 ? Math.round((present / enrolledStudents.length) * 100) : 0;
    const fileSlug = `${(activeBatch.name || 'cohort').replace(/\s+/g, '-')}${selectedTrade ? `-${selectedTrade}` : ''}${selectedLevel ? `-${selectedLevel.replace(/\s+/g, '-')}` : ''}`;
    await generateAttendanceReportPdf({
      title: `Attendance Report — ${activeBatch.name || 'Cohort'}${titleExtra}`,
      meta: [
        { label: 'Cohort', value: activeBatch.name || 'Cohort' },
        { label: 'Field of study', value: getFieldLabel() },
        ...(selectedLevel ? [{ label: 'Level', value: selectedLevel }] : []),
        { label: 'Date', value: selectedDate },
        { label: 'Summary', value: `Present: ${present}   Absent: ${absent}   Rate: ${pct}%` },
      ],
      head: [['#', 'Student Name', 'Field of Study', 'Level', 'Status']],
      body: enrolledStudents.map(({ studentId, fullName, trade, level }, i) => [
        String(i + 1),
        fullName,
        trade || '—',
        level || '—',
        (attendanceByStudent[studentId] || 'present').toUpperCase(),
      ]),
      generatedBy: getGeneratedByFooter(),
      filename: `attendance-${fileSlug}-${selectedDate}.pdf`,
    });
  };

  const exportAttendanceExcel = () => {
    if (!activeBatch || enrolledStudents.length === 0) return;
    const titleExtra = [selectedTrade, selectedLevel].filter(Boolean).map((v) => ` — ${v}`).join('');
    const present = enrolledStudents.filter(({ studentId }) => (attendanceByStudent[studentId] || 'present') === 'present').length;
    const absent = enrolledStudents.length - present;
    const pct = enrolledStudents.length > 0 ? Math.round((present / enrolledStudents.length) * 100) : 0;
    const fileSlug = `${(activeBatch.name || 'cohort').replace(/\s+/g, '-')}${selectedTrade ? `-${selectedTrade}` : ''}${selectedLevel ? `-${selectedLevel.replace(/\s+/g, '-')}` : ''}`;
    const wsData = buildAttendanceExcelRows({
      title: `Attendance Report — ${activeBatch.name || 'Cohort'}${titleExtra}`,
      meta: [
        { label: 'Cohort', value: activeBatch.name || 'Cohort' },
        { label: 'Field of study', value: getFieldLabel() },
        ...(selectedLevel ? [{ label: 'Level', value: selectedLevel }] : []),
        { label: 'Date', value: selectedDate },
        { label: 'Summary', value: `Present: ${present}  |  Absent: ${absent}  |  Rate: ${pct}%` },
      ],
      head: ['#', 'Student Name', 'Field of Study', 'Level', 'Status'],
      body: enrolledStudents.map(({ studentId, fullName, trade, level }, i) => [
        i + 1,
        fullName,
        trade || '—',
        level || '—',
        (attendanceByStudent[studentId] || 'present').toUpperCase(),
      ]),
      generatedBy: getGeneratedByFooter(),
    });
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, `attendance-${fileSlug}-${selectedDate}.xlsx`);
  };

  const getStudentName = r => (r.student && typeof r.student === 'object' ? r.student.fullName : '—');
  const getRecordTrade = r => getTradeFromRecord(r) || '—';
  const getRecordLevel = r => (r.student && typeof r.student === 'object' ? (r.student.level || '—') : '—');

  const sortReviewRecords = (records) =>
    [...(records || [])].sort((a, b) =>
      getStudentName(a).localeCompare(getStudentName(b), undefined, { sensitivity: 'base' })
    );

  const getReviewStats = (records) => {
    const list = Array.isArray(records) ? records : [];
    const present = list.filter((r) => (r.status || 'present') === 'present').length;
    const absent = list.length - present;
    const pct = list.length > 0 ? Math.round((present / list.length) * 100) : 0;
    return { total: list.length, present, absent, pct };
  };

  const getReviewExportMeta = (dateStr) => {
    const titleExtra = [selectedTrade, selectedLevel].filter(Boolean).map((v) => ` — ${v}`).join('');
    const cohortName = activeBatch?.name || 'Cohort';
    return {
      title: `Attendance Report — ${cohortName}${titleExtra}`,
      dateLabel: formatReviewDate(dateStr),
      fileSlug: `${(cohortName).replace(/\s+/g, '-')}${selectedTrade ? `-${selectedTrade}` : ''}${selectedLevel ? `-${selectedLevel.replace(/\s+/g, '-')}` : ''}-${dateStr}`,
    };
  };

  const exportReviewDatePDF = async (dateStr, records) => {
    if (!records?.length) return;
    const { title, dateLabel, fileSlug } = getReviewExportMeta(dateStr);
    const stats = getReviewStats(records);
    const sorted = sortReviewRecords(records);
    await generateAttendanceReportPdf({
      title,
      meta: [
        { label: 'Cohort', value: activeBatch?.name || 'Cohort' },
        { label: 'Field of study', value: getFieldLabel() },
        ...(selectedLevel ? [{ label: 'Level', value: selectedLevel }] : []),
        { label: 'Date', value: dateLabel },
        { label: 'Summary', value: `Present: ${stats.present}   Absent: ${stats.absent}   Rate: ${stats.pct}%` },
      ],
      head: [['#', 'Student Name', 'Field of Study', 'Level', 'Status']],
      body: sorted.map((r, i) => [
        String(i + 1),
        getStudentName(r),
        getRecordTrade(r),
        getRecordLevel(r),
        (r.status || 'present').toUpperCase(),
      ]),
      generatedBy: getGeneratedByFooter(),
      filename: `attendance-report-${fileSlug}.pdf`,
    });
  };

  const exportReviewDateExcel = (dateStr, records) => {
    if (!records?.length) return;
    const { title, dateLabel, fileSlug } = getReviewExportMeta(dateStr);
    const stats = getReviewStats(records);
    const sorted = sortReviewRecords(records);
    const wsData = buildAttendanceExcelRows({
      title,
      meta: [
        { label: 'Cohort', value: activeBatch?.name || 'Cohort' },
        { label: 'Field of study', value: getFieldLabel() },
        ...(selectedLevel ? [{ label: 'Level', value: selectedLevel }] : []),
        { label: 'Date', value: dateLabel },
        { label: 'Summary', value: `Present: ${stats.present}  |  Absent: ${stats.absent}  |  Rate: ${stats.pct}%` },
      ],
      head: ['#', 'Student Name', 'Field of Study', 'Level', 'Status'],
      body: sorted.map((r, i) => [
        i + 1,
        getStudentName(r),
        getRecordTrade(r),
        getRecordLevel(r),
        (r.status || 'present').toUpperCase(),
      ]),
      generatedBy: getGeneratedByFooter(),
    });
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, `attendance-report-${fileSlug}.xlsx`);
  };

  const reviewDateEntries = Object.entries(attendanceByDate).filter(([dateStr]) =>
    !reviewJumpDate || dateStr === reviewJumpDate
  );
  const selectedReviewRecords = expandedDate ? attendanceByDate[expandedDate] || [] : [];
  const selectedReviewStats = getReviewStats(selectedReviewRecords);

  const presentCount = enrolledStudents.filter(({ studentId }) => (attendanceByStudent[studentId] || 'present') === 'present').length;
  const absentCount = enrolledStudents.length - presentCount;
  const attendancePct = enrolledStudents.length > 0 ? Math.round((presentCount / enrolledStudents.length) * 100) : 0;

  const isInstructorView = filterByInstructorId && useInstructorTradeScope;
  const lockedStudentIds = useMemo(() => {
    if (!isInstructorView) return new Set();
    return new Set(existingRecords.map((record) => String(record.student?._id || record.student)));
  }, [existingRecords, isInstructorView]);
  const pendingSaveCount = isInstructorView
    ? enrolledStudents.filter(({ studentId }) => !lockedStudentIds.has(String(studentId))).length
    : enrolledStudents.length;
  const lockedSaveCount = enrolledStudents.length - pendingSaveCount;
  const avatarPalette = isInstructorView
    ? ['#1e40af', '#059669', '#7c3aed', '#ea580c', '#0891b2', '#dc2626']
    : ['linear-gradient(135deg,#8B5CF6,#6366F1)', 'linear-gradient(135deg,#F472B6,#FB923C)', 'linear-gradient(135deg,#34D399,#60A5FA)', 'linear-gradient(135deg,#FBBF24,#F472B6)', 'linear-gradient(135deg,#60A5FA,#8B5CF6)'];
  const getAvatarColor = (name) => avatarPalette[(name?.charCodeAt(0) || 0) % avatarPalette.length];
  const getInitials = (name) => name && name !== '—' ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

  const formatReviewDate = (dateStr) => {
    try { return new Date(dateStr + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return dateStr; }
  };

  return (
    <>
      <style>{`
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .am-root {
          background: var(--bg);
          color: var(--text);
          font-family: var(--font-body), 'Inter', system-ui, -apple-system, sans-serif;
          min-height: 100%;
          padding: 28px 28px 40px;
        }

        /* ── INSTRUCTOR VIEW (clean institutional) ── */
        .am-root--instructor {
          padding: 24px 28px 40px;
          gap: 0;
        }
        .am-instructor-hero {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 24px;
          flex-wrap: wrap;
          margin-bottom: 24px;
          padding: 28px 32px;
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: 12px;
          border-left: 4px solid var(--primary-blue);
          box-shadow: var(--shadow-sm);
        }
        .am-instructor-eyebrow {
          display: inline-block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--primary-blue);
          background: var(--link-hover-bg);
          padding: 4px 10px;
          border-radius: 999px;
          margin-bottom: 10px;
        }
        .am-instructor-title {
          font-size: 26px;
          font-weight: 700;
          color: var(--text-dark);
          letter-spacing: -0.02em;
          line-height: 1.2;
          margin-bottom: 6px;
        }
        .am-instructor-sub {
          font-size: 14px;
          color: var(--text-light);
          max-width: 420px;
          line-height: 1.5;
        }
        .am-instructor-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: stretch;
        }
        .am-meta-chip {
          min-width: 140px;
          padding: 14px 18px;
          background: var(--background-gray);
          border: 1px solid var(--border-light);
          border-radius: 10px;
        }
        .am-meta-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-light);
          margin-bottom: 4px;
        }
        .am-meta-value {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-dark);
        }
        .am-root--instructor .am-tabs {
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: 10px;
          padding: 5px;
          margin-bottom: 20px;
          box-shadow: var(--shadow-sm);
        }
        .am-root--instructor .am-tab.active {
          background: var(--primary-blue);
          color: #ffffff;
          border: none;
          box-shadow: none;
        }
        .am-root--instructor .am-tab:hover:not(.active) {
          background: var(--bg);
          color: var(--text-dark);
        }
        .am-root--instructor .am-controls {
          border-radius: 10px;
          border: 1px solid var(--border-light);
          box-shadow: var(--shadow-sm);
          padding: 20px 24px;
        }
        .am-root--instructor .am-select,
        .am-root--instructor .am-date-input {
          background-color: #ffffff;
          border: 1.5px solid var(--border-medium);
          color: var(--text-dark);
        }
        .am-root--instructor .am-select:focus,
        .am-root--instructor .am-date-input:focus {
          border-color: var(--primary-blue);
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.12);
        }
        .am-root--instructor .am-select:hover:not(:disabled):not(.am-select--readonly),
        .am-root--instructor .am-date-input:hover:not(:disabled) {
          border-color: #9ca3af;
        }
        .am-root--instructor .am-ctrl-refresh {
          background: var(--primary-blue);
          color: #ffffff;
          border: none;
        }
        .am-root--instructor .am-ctrl-refresh:hover:not(:disabled) {
          background: var(--primary-dark);
          color: #ffffff;
        }
        .am-root--instructor .am-stat-card {
          border-radius: 10px;
          box-shadow: var(--shadow-sm);
        }
        .am-root--instructor .am-stat-card::before {
          background: var(--c);
        }
        .am-root--instructor .am-stat-num {
          background: none;
          -webkit-text-fill-color: var(--text-dark);
          color: var(--text-dark);
          font-size: 26px;
        }
        .am-root--instructor .am-progress-wrap {
          border-radius: 10px;
          box-shadow: var(--shadow-sm);
        }
        .am-root--instructor .am-progress-bar-fill {
          background: var(--primary-blue);
        }
        .am-root--instructor .am-progress-pct {
          color: var(--primary-blue);
        }
        .am-root--instructor .am-roll-card {
          border-radius: 10px;
          box-shadow: var(--shadow-sm);
        }
        .am-root--instructor .am-roll-header {
          background: var(--bg);
          border-bottom: 1px solid var(--border-light);
        }
        .am-root--instructor .am-roll-dot {
          background: var(--primary-blue);
          box-shadow: none;
        }
        .am-root--instructor .am-roll-date-chip {
          background: var(--link-hover-bg);
          border-color: #bfdbfe;
          color: var(--primary-blue);
        }
        .am-root--instructor .am-roll-table tbody tr:hover {
          background: var(--bg);
        }
        .am-root--instructor .am-ava {
          border-radius: 8px;
        }
        .am-root--instructor .am-toggle-opt.present-active {
          background: #ecfdf5;
          color: #047857;
          box-shadow: inset 0 0 0 1px #a7f3d0;
        }
        .am-root--instructor .am-toggle-opt.absent-active {
          background: #fef2f2;
          color: #b91c1c;
          box-shadow: inset 0 0 0 1px #fecaca;
        }
        .am-root--instructor .am-roll-footer {
          background: #fafafa;
        }
        .am-root--instructor .am-btn-save {
          background: var(--primary-blue);
          border-radius: 8px;
        }
        .am-root--instructor .am-btn-save:hover:not(:disabled) {
          background: var(--primary-dark);
          transform: none;
        }
        .am-root--instructor .am-quick-present {
          background: #ecfdf5;
          border-color: #a7f3d0;
          color: #047857;
        }
        .am-root--instructor .am-quick-absent {
          background: #fef2f2;
          border-color: #fecaca;
          color: #b91c1c;
        }
        .am-root--instructor .am-review-accordion.open {
          border-color: #bfdbfe;
        }
        .am-root--instructor .am-review-date-icon {
          background: var(--link-hover-bg);
          border-color: #bfdbfe;
        }
        .am-root--instructor .am-review-badge {
          background: var(--link-hover-bg);
          border-color: #bfdbfe;
          color: var(--primary-blue);
        }
        .am-root--instructor .am-hint-banner {
          background: #fffbeb;
          border-color: #fde68a;
          border-radius: 10px;
        }
        .am-root--instructor .am-empty {
          border-radius: 10px;
        }
        .am-root--instructor .am-stat-icon {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.02em;
          color: var(--c);
        }
        @media (max-width: 768px) {
          .am-root--instructor { padding: 16px; }
          .am-instructor-hero { padding: 20px; flex-direction: column; }
          .am-instructor-meta { width: 100%; }
          .am-meta-chip { flex: 1; min-width: 0; }
        }

        /* ── TOAST ── */
        .am-toast {
          position: fixed; top: 20px; right: 20px; z-index: 1000;
          display: flex; align-items: center; gap: 10px;
          padding: 14px 20px; border-radius: 6px;
          font-size: 13px; font-weight: 600; box-shadow: var(--shadow-md);
          animation: amSlide 0.3s ease; max-width: 380px; pointer-events: none;
        }
        .am-toast-s { background: #f0fdf4; border: 1px solid #d1fae5; color: var(--success-green); border-left: 4px solid var(--success-green); }
        .am-toast-e { background: #fef2f2; border: 1px solid #fecaca; color: var(--error-red); border-left: 4px solid var(--error-red); }
        @keyframes amSlide { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }

        /* ── PAGE HEADER ── */
        .am-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 20px; margin-bottom: 28px; flex-wrap: wrap;
        }
        .am-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(139,92,246,0.08); border: 1px solid rgba(139,92,246,0.2);
          border-radius: 12px; padding: 4px 12px;
          font-size: 11px; font-weight: 700; color: var(--accent);
          letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px;
        }
        .am-page-title {
          font-family: inherit;
          font-size: 28px; font-weight: 800; letter-spacing: -0.5px; line-height: 1.1;
        }
        .am-page-title span {
          background: linear-gradient(90deg, var(--accent), var(--error-red));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .am-page-sub { font-size: 13px; color: var(--text-light); margin-top: 6px; }

        /* ── TABS ── */
        .am-tabs {
          display: flex; gap: 4px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 4px;
          width: fit-content; margin-bottom: 28px;
        }
        .am-tab {
          display: flex; align-items: center; gap: 7px;
          padding: 10px 20px; border-radius: 6px;
          font-size: 13px; font-weight: 600; cursor: pointer;
          border: none; background: none; color: var(--text-light);
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .am-tab:hover { color: var(--text-dark); background: var(--border-light); }
        .am-tab.active {
          background: linear-gradient(135deg, rgba(139,92,246,0.2), rgba(99,102,241,0.15));
          color: var(--accent);
          border: 1px solid rgba(139,92,246,0.3);
          box-shadow: var(--shadow-md);
        }
        .am-tab-icon { font-size: 16px; }

        /* ── CONTROLS PANEL ── */
        .am-controls {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; padding: 24px 28px; margin-bottom: 24px;
          display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap;
          box-shadow: var(--shadow-sm);
        }
        .am-ctrl-group { display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 200px; }
        .am-ctrl-label {
          font-size: 11px; font-weight: 700; color: var(--text2);
          text-transform: uppercase; letter-spacing: 0.6px;
        }
        .am-select, .am-date-input {
          background-color: #ffffff;
          border: 1.5px solid #d1d5db;
          border-radius: 8px; padding: 12px 40px 12px 14px;
          font-size: 14px; font-weight: 500; color: var(--text); font-family: inherit;
          outline: none; width: 100%;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
        }
        .am-select:hover:not(:disabled):not(.am-select--readonly),
        .am-date-input:hover:not(:disabled) {
          border-color: #9ca3af;
        }
        .am-select:focus, .am-date-input:focus {
          border-color: #8B5CF6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
        }
        .am-select:disabled,
        .am-select--readonly {
          background-color: #f3f4f6;
          color: #4b5563;
          border-color: var(--border);
          cursor: not-allowed;
          opacity: 1;
          box-shadow: none;
        }
        .am-date-input:disabled {
          background-color: #f3f4f6;
          color: #4b5563;
          border-color: var(--border);
          opacity: 1;
        }
        .am-select {
          appearance: none; cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23374151' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 14px;
        }
        .am-select:disabled,
        .am-select--readonly {
          background-image: none;
          padding-right: 14px;
        }
        .am-select option {
          background: var(--surface);
          color: var(--text);
          font-weight: 500;
        }
        .am-select--placeholder { color: var(--muted); }
        .am-date-input {
          padding-right: 14px;
        }
        .am-date-input::-webkit-calendar-picker-indicator {
          opacity: 0.65;
          cursor: pointer;
          filter: none;
        }

        .am-ctrl-refresh {
          display: flex; align-items: center; gap: 6px; padding: 12px 18px;
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 13px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
          align-self: flex-end;
        }
        .am-ctrl-refresh:hover:not(:disabled) { border-color: rgba(139,92,246,0.3); color: var(--accent); }
        .am-ctrl-refresh:disabled { opacity: 0.4; cursor: not-allowed; }

        /* ── QUICK MARK BAR ── */
        .am-quick-bar {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 20px; flex-wrap: wrap;
        }
        .am-quick-label { font-size: 12px; color: var(--text-light); font-weight: 600; }
        .am-quick-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 12px; cursor: pointer;
          font-size: 12px; font-weight: 700; font-family: inherit;
          border: 1px solid; transition: all 0.2s; white-space: nowrap;
        }
        .am-quick-present { background: rgba(52,211,153,0.1); border-color: rgba(52,211,153,0.3); color: var(--success-green); }
        .am-quick-present:hover { background: rgba(52,211,153,0.18); }
        .am-quick-absent { background: rgba(248,113,113,0.1); border-color: rgba(248,113,113,0.3); color: var(--error-red); }
        .am-quick-absent:hover { background: rgba(248,113,113,0.18); }

        /* ── STATS ROW ── */
        .am-stats-row {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;
          margin-bottom: 20px;
        }
        .am-stat-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 16px 20px;
          display: flex; align-items: center; gap: 14px;
          position: relative; overflow: hidden;
        }
        .am-stat-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: var(--c);
        }
        .am-stat-icon {
          width: 44px; height: 44px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
          background: var(--bg-i);
        }
        .am-stat-num {
          font-family: inherit; font-size: 28px; font-weight: 800; line-height: 1;
          background: linear-gradient(135deg, var(--text), var(--c));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .am-stat-lbl { font-size: 11px; color: var(--text-light); font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px; }

        /* Progress bar */
        .am-progress-wrap {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 16px 20px;
          margin-bottom: 20px; display: flex; align-items: center; gap: 16px;
        }
        .am-progress-label { font-size: 13px; font-weight: 600; white-space: nowrap; }
        .am-progress-bar-track {
          flex: 1; height: 8px; background: var(--surface2);
          border-radius: 12px; overflow: hidden;
        }
        .am-progress-bar-fill {
          height: 100%; border-radius: 12px;
          background: linear-gradient(90deg, var(--accent), var(--success-green));
          transition: width 0.5s ease;
        }
        .am-progress-pct { font-family: inherit; font-size: 15px; font-weight: 700; color: var(--success-green); white-space: nowrap; }

        /* ── ROLL TABLE ── */
        .am-roll-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; overflow: hidden; margin-bottom: 20px;
        }
        .am-roll-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 24px; border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.04));
          flex-wrap: wrap; gap: 10px;
        }
        .am-roll-title {
          font-family: inherit; font-size: 16px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .am-roll-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 8px var(--accent); }
        .am-roll-date-chip {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 12px; border-radius: 12px;
          background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.2);
          font-size: 12px; font-weight: 600; color: var(--accent);
        }

        .am-scroll { overflow-x: auto; }
        .am-scroll::-webkit-scrollbar { height: 4px; }
        .am-scroll::-webkit-scrollbar-thumb { background: var(--border-light); border-radius: 6px; }

        .am-roll-table { width: 100%; border-collapse: collapse; min-width: 420px; }
        .am-roll-table th {
          padding: 12px 20px; font-size: 10px; font-weight: 800; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 1px;
          border-bottom: 1px solid var(--border); text-align: left;
          background: var(--border-light);
        }
        .am-roll-table td {
          padding: 12px 20px; border-bottom: 1px solid var(--border-light);
          vertical-align: middle;
        }
        .am-roll-table tbody tr:last-child td { border-bottom: none; }
        .am-roll-table tbody tr { transition: background 0.15s; }
        .am-roll-table tbody tr:hover { background: rgba(139,92,246,0.04); }

        .am-row-num { font-size: 12px; color: var(--text-light); font-weight: 600; width: 40px; }
        .am-student-cell { display: flex; align-items: center; gap: 10px; }
        .am-ava {
          width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white; font-family: inherit;
        }
        .am-student-name { font-size: 13px; font-weight: 600; }

        /* Toggle button status */
        .am-status-toggle {
          display: flex; border-radius: 6px; overflow: hidden;
          border: 1px solid var(--border-light); width: fit-content;
        }
        .am-toggle-opt {
          padding: 7px 14px; font-size: 12px; font-weight: 700;
          cursor: pointer; border: none; background: var(--surface2);
          color: var(--text-light); font-family: inherit;
          transition: all 0.15s; white-space: nowrap;
        }
        .am-toggle-opt:first-child { border-right: 1px solid var(--border); }
        .am-toggle-opt.present-active {
          background: rgba(52,211,153,0.15); color: var(--success-green);
          box-shadow: inset 0 0 0 1px rgba(52,211,153,0.3);
        }
        .am-toggle-opt.absent-active {
          background: rgba(248,113,113,0.15); color: var(--error-red);
          box-shadow: inset 0 0 0 1px rgba(248,113,113,0.3);
        }
        .am-status-toggle.locked { opacity: 0.92; }
        .am-toggle-opt:disabled {
          cursor: not-allowed; opacity: 0.55;
        }
        .am-saved-badge {
          display: inline-flex; align-items: center; gap: 4px;
          margin-left: 8px; padding: 3px 8px; border-radius: 20px;
          background: var(--bg); border: 1px solid var(--border);
          color: var(--muted); font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.4px;
        }
        .am-roll-table tr.am-row-locked { background: rgba(243,244,246,0.65); }

        /* Roll footer */
        .am-roll-footer {
          display: flex; align-items: center; gap: 10px;
          padding: 18px 24px; border-top: 1px solid var(--border);
          flex-wrap: wrap;
          background: linear-gradient(135deg, rgba(139,92,246,0.04), transparent);
        }
        .am-btn-save {
          display: flex; align-items: center; gap: 8px; padding: 12px 28px;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          color: white; border: none; border-radius: 6px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .am-btn-save:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .am-btn-save:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        .am-btn-export {
          display: flex; align-items: center; gap: 6px; padding: 12px 18px;
          border-radius: 6px; font-size: 13px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s; border: 1px solid;
        }
        .am-btn-pdf { background: rgba(248,113,113,0.1); border-color: rgba(248,113,113,0.25); color: var(--error-red); }
        .am-btn-pdf:hover { background: rgba(248,113,113,0.18); }
        .am-btn-excel { background: rgba(52,211,153,0.1); border-color: rgba(52,211,153,0.25); color: var(--success-green); }
        .am-btn-excel:hover { background: rgba(52,211,153,0.18); }

        /* ── REVIEW / REPORT HUB ── */
        .am-review-hub { display: flex; flex-direction: column; gap: 20px; }

        .am-review-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .am-review-step {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 10px;
          padding: 16px 18px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          box-shadow: var(--shadow-sm);
        }
        .am-review-step-num {
          width: 28px; height: 28px; border-radius: 50%;
          background: linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.1));
          border: 1px solid rgba(139,92,246,0.25);
          color: var(--accent);
          font-size: 12px; font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .am-root--instructor .am-review-step-num {
          background: var(--link-hover-bg);
          border-color: #bfdbfe;
          color: var(--primary-blue);
        }
        .am-review-step-title { font-size: 13px; font-weight: 700; color: var(--text-dark); margin-bottom: 4px; }
        .am-review-step-desc { font-size: 12px; color: var(--text-light); line-height: 1.45; }

        .am-review-toolbar {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 12px;
          padding: 20px 24px;
          box-shadow: var(--shadow-sm);
        }
        .am-review-toolbar-head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
        }
        .am-review-toolbar-title { font-size: 16px; font-weight: 700; color: var(--text-dark); }
        .am-review-toolbar-sub { font-size: 12px; color: var(--text-light); margin-top: 2px; }
        .am-review-toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .am-review-load-btn {
          padding: 10px 16px; border-radius: 8px; border: none;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          color: #fff; font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: opacity 0.2s;
        }
        .am-root--instructor .am-review-load-btn {
          background: var(--primary-blue);
        }
        .am-review-load-btn:hover:not(:disabled) { opacity: 0.92; }
        .am-review-load-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .am-review-filters {
          display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap;
        }

        .am-review-split {
          display: grid;
          grid-template-columns: minmax(260px, 320px) 1fr;
          gap: 16px;
          align-items: start;
        }
        .am-review-sidebar {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }
        .am-review-sidebar-head {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border-light);
          background: #fafafa;
        }
        .am-review-sidebar-title { font-size: 13px; font-weight: 700; color: var(--text-dark); }
        .am-review-sidebar-count { font-size: 11px; color: var(--text-light); margin-top: 2px; }
        .am-review-date-list {
          max-height: 520px;
          overflow-y: auto;
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .am-review-date-card {
          width: 100%;
          text-align: left;
          border: 1.5px solid var(--border-light);
          border-radius: 10px;
          padding: 12px 14px;
          background: var(--surface);
          cursor: pointer;
          font-family: inherit;
          transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
        }
        .am-review-date-card:hover {
          border-color: rgba(139,92,246,0.35);
          background: #faf5ff;
        }
        .am-root--instructor .am-review-date-card:hover {
          border-color: #93c5fd;
          background: var(--bg);
        }
        .am-review-date-card.selected {
          border-color: var(--accent);
          background: linear-gradient(135deg, rgba(139,92,246,0.08), rgba(99,102,241,0.05));
          box-shadow: 0 0 0 3px rgba(139,92,246,0.12);
        }
        .am-root--instructor .am-review-date-card.selected {
          border-color: var(--primary-blue);
          background: var(--link-hover-bg);
          box-shadow: 0 0 0 3px rgba(30,64,175,0.1);
        }
        .am-review-date-card-top {
          display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;
          margin-bottom: 8px;
        }
        .am-review-date-card-day { font-size: 13px; font-weight: 700; color: var(--text-dark); line-height: 1.3; }
        .am-review-date-card-pct {
          font-size: 11px; font-weight: 800; color: var(--accent);
          background: rgba(139,92,246,0.1);
          padding: 2px 8px; border-radius: 999px;
        }
        .am-root--instructor .am-review-date-card-pct {
          color: var(--primary-blue);
          background: #dbeafe;
        }
        .am-review-date-card-bars { display: flex; gap: 6px; flex-wrap: wrap; }
        .am-review-mini-badge {
          font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 999px;
        }
        .am-review-mini-present { background: #ecfdf5; color: #047857; }
        .am-review-mini-absent { background: #fef2f2; color: #b91c1c; }

        .am-review-detail {
          background: var(--background-white);
          border: 1px solid var(--border-light);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          min-height: 320px;
        }
        .am-review-detail-empty {
          padding: 48px 24px;
          text-align: center;
          color: var(--text-light);
        }
        .am-review-detail-empty-icon { font-size: 36px; margin-bottom: 12px; }
        .am-review-detail-empty-title { font-size: 16px; font-weight: 700; color: var(--text-dark); margin-bottom: 6px; }
        .am-review-detail-head {
          padding: 18px 22px;
          border-bottom: 1px solid var(--border-light);
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; flex-wrap: wrap;
          background: linear-gradient(180deg, #fafafa 0%, #fff 100%);
        }
        .am-review-detail-date { font-size: 18px; font-weight: 800; color: var(--text-dark); letter-spacing: -0.02em; }
        .am-review-detail-scope { font-size: 12px; color: var(--text-light); margin-top: 4px; }
        .am-review-detail-stats {
          display: flex; gap: 10px; flex-wrap: wrap; margin-top: 12px;
        }
        .am-review-stat-chip {
          padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
          border: 1px solid;
        }
        .am-review-stat-total { background: #f5f3ff; border-color: #ddd6fe; color: #6d28d9; }
        .am-review-stat-present { background: #ecfdf5; border-color: #a7f3d0; color: #047857; }
        .am-review-stat-absent { background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
        .am-review-detail-export {
          display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
        }
        .am-review-export-hint {
          font-size: 11px; color: var(--text-light); width: 100%; margin-bottom: 4px;
        }

        .am-review-table { width: 100%; border-collapse: collapse; min-width: 400px; }
        .am-review-table th {
          padding: 11px 20px; font-size: 10px; font-weight: 800; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 1px;
          background: var(--border-light); text-align: left;
          border-bottom: 1px solid var(--border);
        }
        .am-review-table td {
          padding: 12px 20px; font-size: 13px; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light);
        }
        .am-review-table tbody tr:last-child td { border-bottom: none; }
        .am-review-table tbody tr:hover { background: var(--border-light); }
        .am-review-course-cell { display: flex; align-items: center; gap: 8px; }
        .am-review-course-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
        .am-root--instructor .am-review-course-dot { background: var(--primary-blue); }

        .am-review-present-badge {
          padding: 4px 10px; border-radius: 12px;
          background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.2);
          color: var(--success-green); font-size: 11px; font-weight: 600;
        }
        .am-review-absent-badge {
          padding: 4px 10px; border-radius: 12px;
          background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.2);
          color: var(--error-red); font-size: 11px; font-weight: 600;
        }

        .am-status-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 12px; border-radius: 12px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
        }
        .am-status-present { background: rgba(52,211,153,0.12); color: #6EE7B7; border: 1px solid rgba(52,211,153,0.25); }
        .am-status-absent  { background: rgba(248,113,113,0.12); color: #FCA5A5; border: 1px solid rgba(248,113,113,0.25); }

        /* ── EMPTY / LOADING ── */
        .am-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 64px 24px; gap: 12px;
          background: var(--background-white); border: 1px solid var(--border-light); border-radius: 12px;
        }
        .am-empty-icon { font-size: 48px; opacity: 0.25; }
        .am-empty-title { font-family: inherit; font-size: 17px; font-weight: 700; color: var(--text-light); }
        .am-empty-sub { font-size: 13px; color: var(--text-light); text-align: center; max-width: 320px; line-height: 1.6; }

        .am-hint-banner {
          margin-bottom: 16px;
          padding: 12px 16px;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          font-size: 13px;
          color: #92400e;
          line-height: 1.5;
        }

        .am-loading { display: flex; align-items: center; justify-content: center; padding: 64px; gap: 8px; }
        .am-dot { width: 8px; height: 8px; border-radius: 50%; animation: amBounce 1.2s ease-in-out infinite; }
        .am-dot:nth-child(1) { background: var(--accent); }
        .am-dot:nth-child(2) { background: var(--error-red); animation-delay: 0.2s; }
        .am-dot:nth-child(3) { background: var(--blue); animation-delay: 0.4s; }
        @keyframes amBounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .am-root { padding: 16px; }
          .am-page-title { font-size: 22px; }
          .am-controls { padding: 18px; gap: 12px; }
          .am-ctrl-group { min-width: 100%; }
          .am-ctrl-refresh { width: 100%; justify-content: center; }
          .am-stats-row { grid-template-columns: repeat(3,1fr); }
          .am-stat-num { font-size: 22px; }
          .am-roll-footer { gap: 8px; }
          .am-btn-save { flex: 1; justify-content: center; }
          .am-tabs { width: 100%; }
          .am-tab { flex: 1; justify-content: center; }
        }
        @media (max-width: 480px) {
          .am-stats-row { grid-template-columns: 1fr 1fr; }
          .am-review-split { grid-template-columns: 1fr; }
          .am-review-steps { grid-template-columns: 1fr; }
          .am-review-detail-head { flex-direction: column; }
          .am-review-filters .am-ctrl-group { min-width: 100%; }
          .am-btn-export { flex: 1; justify-content: center; }
        }
      `}</style>

      {successMessage && <div className="am-toast am-toast-s">✓ {successMessage}</div>}
      {error && <div className="am-toast am-toast-e">⚠ {error}</div>}

      <div className={`am-root${isInstructorView ? ' am-root--instructor' : ''}`}>
        {isInstructorView ? (
          <div className="am-instructor-hero">
            <div>
              <span className="am-instructor-eyebrow">Instructor portal</span>
              <h1 className="am-instructor-title">Attendance</h1>
              <p className="am-instructor-sub">
                Mark who is present or absent for your class today. Students are filtered by your cohort and assigned trades.
              </p>
            </div>
            <div className="am-instructor-meta">
              <div className="am-meta-chip">
                <span className="am-meta-label">Cohort</span>
                <span className="am-meta-value">{instructorContext?.batch?.name || 'No active batch'}</span>
              </div>
              <div className="am-meta-chip">
                <span className="am-meta-label">Your trades</span>
                <span className="am-meta-value">{tradeLabel || 'All in cohort'}</span>
              </div>
              <div className="am-meta-chip">
                <span className="am-meta-label">Today</span>
                <span className="am-meta-value">{new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="am-page-header">
            <div>
              <div className="am-eyebrow">Admin</div>
              <h1 className="am-page-title">Attendance <span>Management</span></h1>
              <p className="am-page-sub">Take roll-call by cohort and field of study — no enrollments required.</p>
            </div>
          </div>
        )}

        <div className="am-tabs">
          <button
            type="button"
            className={`am-tab${viewMode === 'roll' ? ' active' : ''}`}
            onClick={() => setViewMode('roll')}
          >
            {isInstructorView ? 'Take roll' : 'Take Attendance'}
          </button>
          <button
            type="button"
            className={`am-tab${viewMode === 'review' ? ' active' : ''}`}
            onClick={() => setViewMode('review')}
          >
            {isInstructorView ? 'History' : 'Review by Date'}
          </button>
        </div>

        {/* ── ROLL CALL VIEW ── */}
        {viewMode === 'roll' && (
          <>
            {/* Controls */}
            <div className="am-controls">
              <div className="am-ctrl-group">
                <div className="am-ctrl-label">Cohort</div>
                {filterByInstructorId && useInstructorTradeScope ? (
                  <input
                    className="am-select am-select--readonly"
                    value={instructorContext?.batch?.name || 'No running cohort'}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    className={`am-select${!selectedBatchId ? ' am-select--placeholder' : ''}`}
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">— Select cohort —</option>
                    {batches.map((b) => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="am-ctrl-group">
                <div className="am-ctrl-label">Field of Study</div>
                <select
                  className={`am-select${!selectedTrade ? ' am-select--placeholder' : ''}`}
                  value={selectedTrade}
                  onChange={(e) => setSelectedTrade(e.target.value)}
                  disabled={loading || (filterByInstructorId && tradeOptions.length === 0)}
                >
                  <option value="">
                    {filterByInstructorId && useInstructorTradeScope ? 'All assigned trades' : 'All trades'}
                  </option>
                  {tradeOptions.map((trade) => (
                    <option key={trade} value={trade}>{trade}</option>
                  ))}
                </select>
              </div>
              <div className="am-ctrl-group">
                <div className="am-ctrl-label">Level</div>
                <select
                  className={`am-select${!selectedLevel ? ' am-select--placeholder' : ''}`}
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  disabled={loading}
                >
                  <option value="">All levels</option>
                  {ATTENDANCE_LEVEL_OPTIONS.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              <div className="am-ctrl-group">
                <div className="am-ctrl-label">Date</div>
                <input
                  className="am-date-input"
                  type="date"
                  value={selectedDate}
                  max={getTodayDateString()}
                  onChange={e => setSelectedDate(e.target.value)}
                />
              </div>
              <button
                className="am-ctrl-refresh"
                onClick={loadRollCall}
                disabled={loadingRoll || !rollReady}
              >
                {loadingRoll ? '⟳ Loading…' : '↻ Load Roll'}
              </button>
            </div>

            {filterByInstructorId && useInstructorTradeScope && instructorContext?.batch && !tradeLabel && !loading && (
              <div className="am-hint-banner">
                No trades on your account — showing all students in this cohort. Ask admin to assign NIT, SOD, ETEL, or CSA.
              </div>
            )}

            {!filterByInstructorId && batches.length === 0 && !loading ? (
              <div className="am-empty">
                <span className="am-empty-icon">📅</span>
                <div className="am-empty-title">No running cohort</div>
                <div className="am-empty-sub">Open a secondary batch in Batch Management before taking attendance.</div>
              </div>
            ) : filterByInstructorId && useInstructorTradeScope && !instructorContext?.batch && !loading ? (
              <div className="am-empty">
                <span className="am-empty-icon">📅</span>
                <div className="am-empty-title">No running cohort</div>
                <div className="am-empty-sub">Ask admin to open a secondary batch before taking attendance.</div>
              </div>
            ) : !activeBatchId && !loading ? (
              <div className="am-empty">
                <span className="am-empty-icon">📚</span>
                <div className="am-empty-title">Select a cohort</div>
                <div className="am-empty-sub">Choose the running cohort and field of study above to load the roll call.</div>
              </div>
            ) : loadingRoll ? (
              <div className="am-loading"><div className="am-dot" /><div className="am-dot" /><div className="am-dot" /></div>
            ) : enrolledStudents.length === 0 ? (
              <div className="am-empty">
                <span className="am-empty-icon">🎓</span>
                <div className="am-empty-title">No students in this group</div>
                <div className="am-empty-sub">
                  {selectedTrade || selectedLevel
                    ? `No students match${selectedTrade ? ` field “${selectedTrade}”` : ''}${selectedTrade && selectedLevel ? ' and' : ''}${selectedLevel ? ` level “${selectedLevel}”` : ''} in cohort “${activeBatch?.name || 'selected cohort'}”.`
                    : `No secondary students found in cohort “${activeBatch?.name || 'selected cohort'}”. Register students and assign NIT, SOD, ETEL, or CSA as field of study.`}
                </div>
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="am-stats-row">
                  <div className="am-stat-card" style={{ '--c': isInstructorView ? '#1e40af' : '#8B5CF6', '--bg-i': isInstructorView ? '#dbeafe' : 'rgba(139,92,246,0.12)' }}>
                    <div className="am-stat-icon">{isInstructorView ? 'ST' : '🎓'}</div>
                    <div>
                      <div className="am-stat-num">{enrolledStudents.length}</div>
                      <div className="am-stat-lbl">Total</div>
                    </div>
                  </div>
                  <div className="am-stat-card" style={{ '--c': '#059669', '--bg-i': '#d1fae5' }}>
                    <div className="am-stat-icon">{isInstructorView ? 'PR' : '✅'}</div>
                    <div>
                      <div className="am-stat-num">{presentCount}</div>
                      <div className="am-stat-lbl">Present</div>
                    </div>
                  </div>
                  <div className="am-stat-card" style={{ '--c': '#dc2626', '--bg-i': '#fee2e2' }}>
                    <div className="am-stat-icon">{isInstructorView ? 'AB' : '❌'}</div>
                    <div>
                      <div className="am-stat-num">{absentCount}</div>
                      <div className="am-stat-lbl">Absent</div>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="am-progress-wrap">
                  <div className="am-progress-label">Attendance Rate</div>
                  <div className="am-progress-bar-track">
                    <div className="am-progress-bar-fill" style={{ width: `${attendancePct}%` }} />
                  </div>
                  <div className="am-progress-pct">{attendancePct}%</div>
                </div>

                {isInstructorView && lockedSaveCount > 0 && (
                  <div className="am-quick-bar" style={{ marginTop: 0, marginBottom: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
                    <span className="am-quick-label" style={{ color: '#64748b' }}>
                      {lockedSaveCount} student(s) already saved for {selectedDate}. Their attendance cannot be changed.
                    </span>
                  </div>
                )}

                {/* Quick mark */}
                <div className="am-quick-bar">
                  <span className="am-quick-label">Quick actions</span>
                  <button
                    type="button"
                    className="am-quick-btn am-quick-present"
                    onClick={() => markAll('present')}
                    disabled={isInstructorView && pendingSaveCount === 0}
                  >
                    Mark all present
                  </button>
                  <button
                    type="button"
                    className="am-quick-btn am-quick-absent"
                    onClick={() => markAll('absent')}
                    disabled={isInstructorView && pendingSaveCount === 0}
                  >
                    Mark all absent
                  </button>
                </div>

                {/* Roll Table */}
                <div className="am-roll-card">
                  <div className="am-roll-header">
                    <div className="am-roll-title">
                      <span className="am-roll-dot" />
                      {rollScopeLabel} — Roll Call
                    </div>
                    <div className="am-roll-date-chip">{selectedDate}</div>
                  </div>
                  <div className="am-scroll">
                    <table className="am-roll-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th>Student</th>
                          <th>Field of Study</th>
                          <th>Level</th>
                          <th>Attendance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrolledStudents.map(({ studentId, fullName, trade, level }, index) => {
                          const status = attendanceByStudent[studentId] || 'present';
                          const isLocked = lockedStudentIds.has(String(studentId));
                          return (
                            <tr key={studentId} className={isLocked ? 'am-row-locked' : ''}>
                              <td className="am-row-num">{index + 1}</td>
                              <td>
                                <div className="am-student-cell">
                                  <div className="am-ava" style={{ background: getAvatarColor(fullName) }}>
                                    {getInitials(fullName)}
                                  </div>
                                  <span className="am-student-name">{fullName}</span>
                                  {isLocked && <span className="am-saved-badge">Saved</span>}
                                </div>
                              </td>
                              <td>{trade || '—'}</td>
                              <td>{level || '—'}</td>
                              <td>
                                <div className={`am-status-toggle${isLocked ? ' locked' : ''}`}>
                                  <button
                                    type="button"
                                    className={`am-toggle-opt${status === 'present' ? ' present-active' : ''}`}
                                    onClick={() => setStatus(studentId, 'present')}
                                    disabled={isLocked}
                                  >
                                    ✓ Present
                                  </button>
                                  <button
                                    type="button"
                                    className={`am-toggle-opt${status === 'absent' ? ' absent-active' : ''}`}
                                    onClick={() => setStatus(studentId, 'absent')}
                                    disabled={isLocked}
                                  >
                                    ✕ Absent
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="am-roll-footer">
                    <button
                      type="button"
                      className="am-btn-save"
                      onClick={saveAttendance}
                      disabled={saving || (isInstructorView && pendingSaveCount === 0)}
                    >
                      {saving
                        ? 'Saving…'
                        : isInstructorView && pendingSaveCount === 0
                          ? 'All students saved'
                          : isInstructorView
                            ? `Save attendance (${pendingSaveCount})`
                            : 'Save attendance'}
                    </button>
                    {!filterByInstructorId && (
                      <>
                        <button type="button" className="am-btn-export am-btn-pdf" onClick={exportAttendancePDF}>
                          Export PDF
                        </button>
                        <button type="button" className="am-btn-export am-btn-excel" onClick={exportAttendanceExcel}>
                          Export Excel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── REVIEW / REPORT HUB ── */}
        {viewMode === 'review' && (
          <div className="am-review-hub">
            <div className="am-review-steps">
              <div className="am-review-step">
                <span className="am-review-step-num">1</span>
                <div>
                  <div className="am-review-step-title">Set your filters</div>
                  <div className="am-review-step-desc">Choose the cohort and field of study to narrow the report.</div>
                </div>
              </div>
              <div className="am-review-step">
                <span className="am-review-step-num">2</span>
                <div>
                  <div className="am-review-step-title">Pick a date</div>
                  <div className="am-review-step-desc">Select a day from the history list on the left to open its details.</div>
                </div>
              </div>
              <div className="am-review-step">
                <span className="am-review-step-num">3</span>
                <div>
                  <div className="am-review-step-title">Export the report</div>
                  <div className="am-review-step-desc">Download PDF or Excel for the selected date to share or archive.</div>
                </div>
              </div>
            </div>

            <div className="am-review-toolbar">
              <div className="am-review-toolbar-head">
                <div>
                  <div className="am-review-toolbar-title">Attendance reports</div>
                  <div className="am-review-toolbar-sub">
                    {activeBatch?.name || 'No cohort selected'}
                    {selectedTrade ? ` · ${selectedTrade}` : tradeLabel ? ` · ${tradeLabel}` : ''}
                    {selectedLevel ? ` · ${selectedLevel}` : ''}
                  </div>
                </div>
                <div className="am-review-toolbar-actions">
                  <button
                    type="button"
                    className="am-review-load-btn"
                    onClick={loadReviewByDate}
                    disabled={reviewLoading || !rollReady}
                  >
                    {reviewLoading ? 'Loading…' : '↻ Refresh history'}
                  </button>
                </div>
              </div>
              <div className="am-review-filters">
                <div className="am-ctrl-group">
                  <div className="am-ctrl-label">Cohort</div>
                  {filterByInstructorId && useInstructorTradeScope ? (
                    <input
                      className="am-select am-select--readonly"
                      value={instructorContext?.batch?.name || 'No running cohort'}
                      readOnly
                      disabled
                    />
                  ) : (
                    <select
                      className={`am-select${!selectedBatchId ? ' am-select--placeholder' : ''}`}
                      value={selectedBatchId}
                      onChange={(e) => setSelectedBatchId(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">— Select cohort —</option>
                      {batches.map((b) => (
                        <option key={b._id} value={b._id}>{b.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="am-ctrl-group">
                  <div className="am-ctrl-label">Field of Study</div>
                  <select
                    className={`am-select${!selectedTrade ? ' am-select--placeholder' : ''}`}
                    value={selectedTrade}
                    onChange={(e) => setSelectedTrade(e.target.value)}
                    disabled={loading || (filterByInstructorId && tradeOptions.length === 0)}
                  >
                    <option value="">
                      {filterByInstructorId && useInstructorTradeScope ? 'All assigned trades' : 'All trades'}
                    </option>
                    {tradeOptions.map((trade) => (
                      <option key={trade} value={trade}>{trade}</option>
                    ))}
                  </select>
                </div>
                <div className="am-ctrl-group">
                  <div className="am-ctrl-label">Level</div>
                  <select
                    className={`am-select${!selectedLevel ? ' am-select--placeholder' : ''}`}
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">All levels</option>
                    {ATTENDANCE_LEVEL_OPTIONS.map((level) => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                <div className="am-ctrl-group">
                  <div className="am-ctrl-label">Jump to date</div>
                  <input
                    className="am-date-input"
                    type="date"
                    value={reviewJumpDate}
                    max={getTodayDateString()}
                    onChange={(e) => {
                      setReviewJumpDate(e.target.value);
                      if (e.target.value && attendanceByDate[e.target.value]) {
                        setExpandedDate(e.target.value);
                      }
                    }}
                  />
                </div>
                {reviewJumpDate && (
                  <div className="am-ctrl-group" style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <button
                      type="button"
                      className="am-ctrl-refresh"
                      onClick={() => { setReviewJumpDate(''); }}
                    >
                      Clear date filter
                    </button>
                  </div>
                )}
              </div>
            </div>

            {!rollReady && !loading ? (
              <div className="am-empty">
                <span className="am-empty-icon">📅</span>
                <div className="am-empty-title">No running cohort</div>
                <div className="am-empty-sub">
                  {isInstructorView
                    ? 'Ask admin to open a secondary batch before viewing attendance history.'
                    : 'Open a secondary batch in Batch Management, then return here.'}
                </div>
              </div>
            ) : reviewLoading ? (
              <div className="am-loading"><div className="am-dot" /><div className="am-dot" /><div className="am-dot" /></div>
            ) : Object.keys(attendanceByDate).length === 0 ? (
              <div className="am-empty">
                <span className="am-empty-icon">📅</span>
                <div className="am-empty-title">No attendance history yet</div>
                <div className="am-empty-sub">
                  Go to {isInstructorView ? 'Take roll' : 'Take Attendance'}, mark students for a day, then come back here to review and export.
                </div>
              </div>
            ) : reviewDateEntries.length === 0 ? (
              <div className="am-empty">
                <span className="am-empty-icon">🔍</span>
                <div className="am-empty-title">No records on that date</div>
                <div className="am-empty-sub">Try another date or clear the date filter to see all history.</div>
              </div>
            ) : (
              <div className="am-review-split">
                <aside className="am-review-sidebar">
                  <div className="am-review-sidebar-head">
                    <div className="am-review-sidebar-title">Attendance by date</div>
                    <div className="am-review-sidebar-count">
                      {reviewDateEntries.length} day{reviewDateEntries.length !== 1 ? 's' : ''} with records
                    </div>
                  </div>
                  <div className="am-review-date-list">
                    {reviewDateEntries.map(([dateStr, records]) => {
                      const stats = getReviewStats(records);
                      const isSelected = expandedDate === dateStr;
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          className={`am-review-date-card${isSelected ? ' selected' : ''}`}
                          onClick={() => setExpandedDate(dateStr)}
                        >
                          <div className="am-review-date-card-top">
                            <div className="am-review-date-card-day">{formatReviewDate(dateStr)}</div>
                            <span className="am-review-date-card-pct">{stats.pct}%</span>
                          </div>
                          <div className="am-review-date-card-bars">
                            <span className="am-review-mini-badge am-review-mini-present">✓ {stats.present} present</span>
                            <span className="am-review-mini-badge am-review-mini-absent">✕ {stats.absent} absent</span>
                            <span className="am-review-mini-badge" style={{ background: '#f3f4f6', color: '#4b5563' }}>
                              {stats.total} total
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </aside>

                <section className="am-review-detail">
                  {!expandedDate || selectedReviewRecords.length === 0 ? (
                    <div className="am-review-detail-empty">
                      <div className="am-review-detail-empty-icon">📋</div>
                      <div className="am-review-detail-empty-title">Select a date to view the report</div>
                      <p>Choose a day from the list on the left to see student details and export options.</p>
                    </div>
                  ) : (
                    <>
                      <div className="am-review-detail-head">
                        <div>
                          <div className="am-review-detail-date">{formatReviewDate(expandedDate)}</div>
                          <div className="am-review-detail-scope">
                            {rollScopeLabel} · {selectedReviewStats.total} student{selectedReviewStats.total !== 1 ? 's' : ''}
                          </div>
                          <div className="am-review-detail-stats">
                            <span className="am-review-stat-chip am-review-stat-total">{selectedReviewStats.total} recorded</span>
                            <span className="am-review-stat-chip am-review-stat-present">✓ {selectedReviewStats.present} present</span>
                            <span className="am-review-stat-chip am-review-stat-absent">✕ {selectedReviewStats.absent} absent</span>
                            <span className="am-review-stat-chip am-review-stat-total">{selectedReviewStats.pct}% rate</span>
                          </div>
                        </div>
                        <div className="am-review-detail-export">
                          <span className="am-review-export-hint">Export report for this date</span>
                          <button
                            type="button"
                            className="am-btn-export am-btn-pdf"
                            onClick={() => exportReviewDatePDF(expandedDate, selectedReviewRecords)}
                          >
                            Export PDF
                          </button>
                          <button
                            type="button"
                            className="am-btn-export am-btn-excel"
                            onClick={() => exportReviewDateExcel(expandedDate, selectedReviewRecords)}
                          >
                            Export Excel
                          </button>
                        </div>
                      </div>
                      <div className="am-scroll">
                        <table className="am-review-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px' }}>#</th>
                              <th>Student</th>
                              <th>Field of Study</th>
                              <th>Level</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortReviewRecords(selectedReviewRecords).map((r, index) => (
                              <tr key={r._id || `${expandedDate}-${index}`}>
                                <td className="am-row-num">{index + 1}</td>
                                <td>
                                  <div className="am-student-cell">
                                    <div
                                      className="am-ava"
                                      style={{
                                        background: getAvatarColor(getStudentName(r)),
                                        width: '28px',
                                        height: '28px',
                                        fontSize: '10px',
                                        borderRadius: '7px',
                                      }}
                                    >
                                      {getInitials(getStudentName(r))}
                                    </div>
                                    {getStudentName(r)}
                                  </div>
                                </td>
                                <td>
                                  <div className="am-review-course-cell">
                                    <span className="am-review-course-dot" />
                                    {getRecordTrade(r)}
                                  </div>
                                </td>
                                <td>{getRecordLevel(r)}</td>
                                <td>
                                  <span className={`am-status-pill am-status-${(r.status || 'present').toLowerCase()}`}>
                                    {(r.status || 'present') === 'present' ? '✓' : '✕'}{' '}
                                    {(r.status || 'present').charAt(0).toUpperCase() + (r.status || 'present').slice(1)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AttendanceManagement;